import {
  formatDate as formatDateShared,
  formatCurrency as formatCurrencyShared,
  formatPercentage as formatPercentageShared,
} from '../../utils/formatUtils';

/**
 * Utilidades para el módulo de planes
 */

/**
 * Formatea una fecha para su visualización
 * @param {string} dateString - Fecha en formato ISO
 * @returns {string} Fecha formateada
 */
export const formatDate = (dateString) => {
  if (!dateString) return '';
  return formatDateShared(dateString, { fallback: '' });
};

/**
 * Formatea una fecha para input de tipo date
 * @param {string} dateString - Fecha en formato ISO
 * @returns {string} Fecha en formato YYYY-MM-DD
 */
export const formatDateForInput = (dateString) => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  } catch (error) {
    return '';
  }
};

/**
 * Valida si un valor es un decimal válido entre 0 y 1
 * @param {string|number} value - Valor a validar
 * @returns {boolean} True si es válido
 */
export const isValidDecimal = (value) => {
  const number = parseFloat(value);
  return !isNaN(number) && number > 0 && number < 1;
};

/**
 * Formatea un número como moneda
 * @param {number} amount - Cantidad a formatear
 * @returns {string} Cantidad formateada
 */
export const formatCurrency = (amount) => formatCurrencyShared(amount, { fallback: '' });

/**
 * Formatea un porcentaje
 * @param {number} percentage - Porcentaje a formatear
 * @returns {string} Porcentaje formateado
 */
export const formatPercentage = (percentage) =>
  formatPercentageShared(percentage, { decimals: 3, fallback: '' });

/**
 * Valida los campos requeridos de un plan
 * @param {Object} planData - Datos del plan a validar
 * @returns {Object} Objeto con errores encontrados
 */
export const validatePlanData = (planData) => {
  const errors = {};

  if (!planData.name?.trim()) {
    errors.name = 'El nombre es requerido';
  }

  if (!planData.effective_start_date) {
    errors.effective_start_date = 'La fecha de inicio es requerida';
  }

  if (!planData.installments_number || planData.installments_number <= 0) {
    errors.installments_number = 'La cantidad de cuotas debe ser mayor a 0';
  }

  if (!planData.contracts_number || planData.contracts_number <= 0) {
    errors.contracts_number = 'La cantidad de contratos debe ser mayor a 0';
  }

  if (!planData.interest_rate || planData.interest_rate < 0) {
    errors.interest_rate = 'La tasa de interés debe ser mayor o igual a 0';
  }

  if (!planData.first_installment_amount || planData.first_installment_amount <= 0) {
    errors.first_installment_amount = 'El valor de la primera cuota debe ser mayor a 0';
  }

  return errors;
};

/**
 * Transforma los datos del plan para el API
 * @param {Object} planData - Datos del formulario
 * @param {Object} branch - Información de la sucursal
 * @param {string} userUuid - UUID del usuario
 * @returns {Object} Datos transformados para el API
 */
export const transformPlanDataForAPI = (planData, branch, userUuid) => {
  return {
    name: planData.name?.trim(),
    effective_start_date: planData.effective_start_date,
    installments_number: parseInt(planData.installments_number, 10),
    contracts_number: parseInt(planData.contracts_number, 10),
    interest_rate: parseFloat(planData.interest_rate),
    first_installment_amount: parseFloat(planData.first_installment_amount),
    uuid_branch: branch?.uuid || branch?.uuid_branch,
    uuid_user: userUuid,
  };
};

/**
 * Filtra planes por término de búsqueda
 * @param {Array} plans - Array de planes
 * @param {string} searchTerm - Término de búsqueda
 * @returns {Array} Planes filtrados
 */
export const filterPlansBySearch = (plans, searchTerm) => {
  if (!searchTerm?.trim()) return plans;

  const term = searchTerm.toLowerCase().trim();

  return plans.filter(
    (plan) =>
      plan.name?.toLowerCase().includes(term) ||
      plan.branch?.description?.toLowerCase().includes(term)
  );
};

/**
 * Obtiene los elementos de la página actual
 * @param {Array} items - Array de elementos
 * @param {number} page - Página actual (1-based)
 * @param {number} itemsPerPage - Elementos por página
 * @returns {Array} Elementos de la página actual
 */
export const getCurrentPageItems = (items, page, itemsPerPage) => {
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  return items.slice(startIndex, endIndex);
};

/**
 * Calcula el número total de páginas
 * @param {number} totalItems - Total de elementos
 * @param {number} itemsPerPage - Elementos por página
 * @returns {number} Número total de páginas
 */
export const getTotalPages = (totalItems, itemsPerPage) => {
  return Math.ceil(totalItems / itemsPerPage);
};

/**
 * Normaliza un valor booleano desde string
 * @param {string|boolean|number} value - Valor a normalizar
 * @returns {boolean} Valor booleano
 */
export const normalizeBooleanValue = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') return value === '1' || value.toLowerCase() === 'true';
  return false;
};

/**
 * Genera un objeto de estado inicial para items de tabs
 * @param {string} planUuid - UUID del plan
 * @returns {Object} Estado inicial
 */
export const generateInitialItemState = (planUuid) => ({
  uuid_plan: planUuid,
  name: '',
  created_at: new Date().toISOString(),
});

/**
 * Maneja errores de API y los transforma en mensajes legibles
 * @param {Error} error - Error de la API
 * @returns {string} Mensaje de error legible
 */
export const handleAPIError = (error) => {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  if (error?.message) {
    return error.message;
  }

  return 'Ha ocurrido un error inesperado';
};
